.frame {
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  min-inline-size: 0;
}

/* The table scrolls inside its frame (both axes), so the page itself never scrolls sideways and
   the sticky header stays visible while rows scroll. */
.scroller {
  overflow: auto;
  max-block-size: min(70dvh, 52rem);
  border-radius: var(--radius-md);
}

.table {
  inline-size: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: var(--text-md);
}

.table th {
  position: sticky;
  inset-block-start: 0;
  z-index: 1;
  padding-block: var(--space-2);
  padding-inline: var(--space-4);
  background: var(--color-surface-sunken);
  border-block-end: var(--border-width) solid var(--color-border);
  color: var(--color-text-muted);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  letter-spacing: 0.02em;
  text-align: start;
  white-space: nowrap;
}

.table td {
  padding-block: var(--space-3);
  padding-inline: var(--space-4);
  border-block-end: var(--border-width) solid var(--color-rule);
  vertical-align: middle;
}

.table tbody tr:last-child td {
  border-block-end: 0;
}

.end {
  text-align: end !important;
}

.clickable {
  cursor: pointer;
}

.clickable:hover td {
  background: var(--color-surface-hover);
}

.cellLabel {
  display: none;
}

.primaryLink {
  font-weight: var(--weight-semibold);
  color: var(--color-link);
  text-decoration: none;
}

.primaryLink:hover {
  text-decoration: underline;
}

.muted {
  color: var(--color-text-muted);
}

.skeletonRow td {
  padding-block: var(--space-4);
}

.footer {
  border-block-start: var(--border-width) solid var(--color-border);
}

/* Single-row selection: a native radio at the row end; the selected row is tinted. The column is
   sticky at the inline end of the frame (RI-02): when the columns are wider than the frame (English,
   tablets), the rows scroll under it and the radio stays in view. */
.table th.selectHeader,
.table td.selectCell {
  position: sticky;
  inset-inline-end: 0;
  inline-size: 3rem;
  border-inline-start: var(--border-width) solid var(--color-rule);
}

.table th.selectHeader {
  z-index: 2;
}

.table td.selectCell {
  padding-block: 0;
  padding-inline: var(--space-1);
  background: var(--color-surface);
  text-align: center;
}

.clickable:hover td.selectCell {
  background: var(--color-surface-hover);
}

.selectHit {
  display: grid;
  place-items: center;
  min-inline-size: 2.75rem;
  min-block-size: 2.75rem;
  cursor: pointer;
}

.radio {
  inline-size: 1.125rem;
  block-size: 1.125rem;
  margin: 0;
  accent-color: var(--color-primary);
  cursor: pointer;
}

.table tbody tr:has(.radio:checked) td {
  background: var(--color-primary-subtle);
}

/* Phones: rows become stacked cards with a label per value, in each column's phone order; the
   card title (primary cell) and the selection radio share the first line. */
@media (max-width: 40rem) {
  .scroller {
    max-block-size: none;
    overflow: visible;
  }

  .table thead {
    position: absolute;
    inline-size: 1px;
    block-size: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }

  .table,
  .table tbody {
    display: block;
  }

  .table tr {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    column-gap: var(--space-2);
    padding-block: var(--space-3);
    padding-inline: var(--space-4);
    border-block-end: var(--border-width) solid var(--color-rule);
  }

  .table tbody tr:last-child {
    border-block-end: 0;
  }

  .table td {
    display: grid;
    grid-column: 1 / -1;
    grid-template-columns: minmax(6.5rem, 38%) 1fr;
    gap: var(--space-3);
    padding: var(--space-1) 0;
    border: 0;
    text-align: start !important;
    overflow-wrap: anywhere;
  }

  .table td.primaryCell {
    grid-row: 1;
    grid-column: 1;
    grid-template-columns: 1fr;
    align-self: center;
    padding-block-end: var(--space-2);
    font-size: var(--text-lg);
  }

  .table td.selectCell {
    position: static;
    display: block;
    grid-row: 1;
    grid-column: 2;
    inline-size: auto;
    padding: 0;
    border: 0;
    background: transparent;
  }

  .table tbody tr:has(.radio:checked) td {
    background: transparent;
  }

  .table tbody tr:has(.radio:checked) {
    background: var(--color-primary-subtle);
  }

  .cellLabel {
    display: block;
    color: var(--color-text-muted);
    font-size: var(--text-sm);
  }

  .primaryCell .cellLabel {
    display: none;
  }

  .clickable:hover td,
  .clickable:hover td.selectCell {
    background: transparent;
  }
}

/* Pagination */
.pagination {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
}

.paginationInfo {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.paginationControls {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.pageSize {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.pageSize select {
  min-block-size: var(--control-height-sm);
  padding-inline: var(--space-2);
  border: var(--border-width) solid var(--color-border-strong);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
}
